import { useEffect } from 'react';
import { 
  Users, 
  TrendingUp, 
  Clock, 
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { useStore } from '../lib/store';
import { PIPELINE_STAGES, normalizeStatus } from '../lib/pocketbase';
import { format, subDays, startOfDay, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { parseDate, cn } from '../lib/utils';

export default function Dashboard() {
  const { leads, clients, stats, alerts, fetchLeads, fetchClients, fetchStats, fetchAlerts } = useStore();
  
  useEffect(() => {
    fetchLeads().then(() => {
      fetchStats();
      fetchAlerts(7);
    });
    fetchClients();
  }, []);
  
  // Client stats
  const clientCount = clients.length;
  const leadCount = leads.length;
  const totalSubmissions = clientCount + leadCount;
  const conversionRate = totalSubmissions > 0 ? (clientCount / totalSubmissions) * 100 : 0;
  const monthlyRevenue = clients.reduce((sum: number, c: any) => sum + (c.monthly_amount || 0), 0);
  
  // Prepare chart data
  const pieData = stats ? PIPELINE_STAGES.map(stage => ({
    name: stage.label,
    value: stats.byStage[stage.id] || 0,
  })) : [];
  
  const COLORS = ['#3b82f6', '#f59e0b', '#6366f1', '#22c55e', '#ef4444'];
  
  // Leads per day for the last 14 days
  const last14Days = eachDayOfInterval({
    start: subDays(new Date(), 13),
    end: new Date(),
  });
  
  const leadsPerDay = last14Days.map(day => {
    const dayStart = startOfDay(day);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    const count = leads.filter(l => {
      const created = parseDate(l.created);
      return created && created >= dayStart && created < dayEnd;
    }).length;
    return {
      date: format(day, 'dd/MM'),
      leads: count,
    };
  });
  
  // Weekly conversion data — only real data for current week
  const weeklyData = [
    { week: 'Cette semaine', leads: stats?.leadsThisWeek || 0, converted: stats?.byStage?.converti || 0 },
  ];
  
  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Vue d'ensemble de votre activité</p>
        </div>
        <div className="text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-200">
          {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
        </div>
      </div>
      
      {/* Alert: sites to validate */}
      {(() => {
        const toValidate = clients.filter((c: any) => ['built', 'built_auto'].includes(c.site_status) && c.site_url);
        if (toValidate.length === 0) return null;
        return (
          <Link to="/orders" className="block">
            <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 flex items-center gap-4 animate-pulse hover:bg-orange-100 transition cursor-pointer">
              <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-bold text-orange-800">
                  🚨 {toValidate.length} site{toValidate.length > 1 ? 's' : ''} à valider !
                </p>
                <p className="text-sm text-orange-600">
                  {toValidate.map((c: any) => c.business_name).join(', ')} — Cliquez pour voir les commandes
                </p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-orange-400" />
            </div>
          </Link>
        );
      })()}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{clientCount}</p>
          <p className="text-sm text-gray-500 mt-1">Clients</p>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            {stats?.leadsThisWeek ? (
              <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                <ArrowUpRight className="w-4 h-4" />
                +{stats.leadsThisWeek}
              </span>
            ) : null}
          </div>
          <p className="text-2xl font-bold text-gray-900">{leadCount}</p>
          <p className="text-sm text-gray-500 mt-1">Leads non convertis</p>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {conversionRate.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-500 mt-1">Taux de conversion</p>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-green-600 text-2xl font-bold">{monthlyRevenue}€</p>
          <p className="text-sm text-gray-500 mt-1">Revenue mensuel</p>
        </div>
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads per day */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Leads par jour</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={leadsPerDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 11, fill: '#9ca3af' }} 
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#9ca3af' }} 
                allowDecimals={false} 
                axisLine={false}
                tickLine={false}
              />
              <Tooltip 
                contentStyle={{ 
                  borderRadius: '8px', 
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
              />
              <Bar dataKey="leads" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Pipeline distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Répartition pipeline</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  borderRadius: '8px', 
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Conversion trend */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Évolution hebdomadaire</h2>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={weeklyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis 
              dataKey="week" 
              tick={{ fontSize: 11, fill: '#9ca3af' }} 
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#9ca3af' }} 
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip 
              contentStyle={{ 
                borderRadius: '8px', 
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '12px' }}
            />
            <Line 
              type="monotone" 
              dataKey="leads" 
              name="Nouveaux leads"
              stroke="#f97316" 
              strokeWidth={2}
              dot={{ fill: '#f97316', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
            <Line 
              type="monotone" 
              dataKey="converted" 
              name="Conversions"
              stroke="#22c55e" 
              strokeWidth={2}
              dot={{ fill: '#22c55e', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-amber-900">
                Leads à relancer
              </h2>
              <p className="text-sm text-amber-700">
                {alerts.length} lead{alerts.length > 1 ? 's' : ''} sans contact depuis plus de 7 jours
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 5).map(({ lead, daysSinceContact }) => (
              <Link
                key={lead.id}
                to="/leads"
                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-amber-100/50 transition-colors border border-amber-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-medium text-sm">
                    {(lead.name || lead.company || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{lead.name || lead.company}</p>
                    <p className="text-xs text-gray-500">{lead.activity}</p>
                  </div>
                </div>
                <span className="text-sm text-amber-700 font-medium bg-amber-100 px-2.5 py-1 rounded-full">
                  {daysSinceContact}j
                </span>
              </Link>
            ))}
          </div>
          {alerts.length > 5 && (
            <Link 
              to="/alerts"
              className="block text-center text-sm text-amber-700 hover:text-amber-800 font-medium mt-4"
            >
              Voir tous les leads à relancer →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
